import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import Notification from '../components/Notification';
import { useNotification } from '../hooks/useNotification';

const Districts = () => {
  const navigate = useNavigate();
  const [districts, setDistricts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState(null);
  const [name, setName] = useState('');
  const [assigningDistrict, setAssigningDistrict] = useState(null);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, district: null });
  const { notification, showError, showSuccess, showWarning, hideNotification } = useNotification();
  const [createManager, setCreateManager] = useState(false);
  const [managerForm, setManagerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
  });
  const [districtManagers, setDistrictManagers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showAddManagerForm, setShowAddManagerForm] = useState(false);
  const [assignExistingUser, setAssignExistingUser] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [districtRes, schoolRes] = await Promise.all([
        api.get('/admin/districts'),
        api.get('/admin/schools'),
      ]);
      setDistricts(districtRes.data.data.districts || []);
      setSchools(schoolRes.data.data.schools || []);
    } catch (error) {
      console.error('Failed to load districts:', error);
      showError('Failed to load district data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingDistrict(null);
    setName('');
    setCreateManager(false);
    setManagerForm({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phoneNumber: '',
    });
    setShowModal(true);
  };

  const openEditModal = async (district) => {
    setEditingDistrict(district);
    setName(district.name);
    setCreateManager(false);
    setShowAddManagerForm(false);
    setAssignExistingUser(false);
    setSelectedUserId('');
    setManagerForm({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phoneNumber: '',
    });
    
    try {
      const usersResponse = await api.get('/admin/users', {
        params: { role: 'DISTRICT', limit: 1000 },
      });
      const allDistrictUsers = usersResponse.data.data.users || [];
      const managers = allDistrictUsers.filter(
        (user) => user.districtId === district.id
      );
      setDistrictManagers(managers);
      
      const allUsersResponse = await api.get('/admin/users', {
        params: { limit: 1000 },
      });
      const allUsers = allUsersResponse.data.data.users || [];
      const available = allUsers.filter(
        (user) => user.districtId !== district.id && (user.role !== 'DISTRICT' || !user.districtId)
      );
      setAvailableUsers(available);
    } catch (error) {
      console.error('Failed to load district managers:', error);
      setDistrictManagers([]);
      setAvailableUsers([]);
    }
    
    setShowModal(true);
  };

  const handleSaveDistrict = async (e) => {
    e.preventDefault();
    try {
      let districtId;
      if (editingDistrict) {
        await api.patch(`/admin/districts/${editingDistrict.id}`, { name });
        districtId = editingDistrict.id;
      } else {
        const response = await api.post('/admin/districts', { name });
        districtId = response.data.data.district.id;
      }

      if (createManager && managerForm.firstName && managerForm.lastName && managerForm.email && managerForm.password) {
        try {
          await api.post('/admin/users', {
            firstName: managerForm.firstName,
            lastName: managerForm.lastName,
            email: managerForm.email,
            password: managerForm.password,
            phoneNumber: managerForm.phoneNumber || undefined,
            role: 'DISTRICT',
            status: 'ACTIVE',
            districtId: districtId,
            schoolDistrict: name, 
          });
        } catch (userError) {
          console.error('Failed to create district manager:', userError);
          showWarning(userError.response?.data?.message || 'District created but failed to create manager. You can assign one later.');
        }
      }

      setShowModal(false);
      showSuccess(editingDistrict ? 'District updated successfully' : 'District created successfully');
      await fetchData();
    } catch (error) {
      console.error('Failed to save district:', error);
      showError(error.response?.data?.message || 'Failed to save district');
    }
  };

  const handleDeleteClick = (district) => {
    setDeleteConfirm({ isOpen: true, district });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.district) return;
    
    try {
      await api.delete(`/admin/districts/${deleteConfirm.district.id}`);
      setDeleteConfirm({ isOpen: false, district: null });
      showSuccess('District deleted successfully');
      await fetchData();
    } catch (error) {
      console.error('Failed to delete district:', error);
      showError(error.response?.data?.message || 'Failed to delete district');
      setDeleteConfirm({ isOpen: false, district: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, district: null });
  };

  const openAssignModal = (district) => {
    setAssigningDistrict(district);
    const assignedSchools = schools.filter((s) => s.district === district.name);
    setSelectedSchoolIds(assignedSchools.map((s) => s.id));
  };

  const toggleSchoolSelection = (schoolId) => {
    setSelectedSchoolIds((prev) =>
      prev.includes(schoolId) ? prev.filter((id) => id !== schoolId) : [...prev, schoolId]
    );
  };

  const handleAssign = async () => {
    try {
      await api.post(`/admin/districts/${assigningDistrict.id}/assign`, {
        schoolIds: selectedSchoolIds,
      });
      setAssigningDistrict(null);
      setSelectedSchoolIds([]);
      showSuccess('Schools assigned to district successfully');
      await fetchData();
    } catch (error) {
      console.error('Failed to assign schools:', error);
      showError(error.response?.data?.message || 'Failed to assign schools');
    }
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">District Management</h1>
          <p className="text-gray-600">
            View district-level data and manage districts assigned to schools.
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primaryDark transition-colors"
        >
          + Add District
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading district data...</p>
          </div>
        </div>
      ) : districts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-md">
          <p className="text-gray-500">No districts found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {districts.map((district) => (
            <div
              key={district.id}
              onClick={() => navigate(`/districts/${district.id}`)}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-gray-800">{district.name}</h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditModal(district);
                  }}
                  className="text-primary hover:text-primaryDark"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
              </div>
              <div className="space-y-2 text-sm">
                {district.districtManager && (
                  <div className="mb-4 pb-4 border-b border-gray-200 bg-gray-50 rounded-lg p-3 -mx-1">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2 tracking-wide">District Manager</p>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-gray-900">
                        {district.districtManager.firstName} {district.districtManager.lastName}
                      </p>
                      <p className="text-xs text-gray-600 flex items-center">
                        <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {district.districtManager.email}
                      </p>
                      {district.districtManager.phoneNumber && (
                        <p className="text-xs text-gray-600 flex items-center">
                          <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {district.districtManager.phoneNumber}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <p className="text-gray-600">
                  <span className="font-medium">Schools:</span> {district.totalSchools}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Total Users:</span> {district.totalUsers}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Active Users:</span> {district.activeUsers}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Completed Enrollments:</span>{' '}
                  {district.completedEnrollments}
                </p>
                <div className="pt-2 border-t border-gray-200 flex justify-between items-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(district);
                    }}
                    className="text-xs text-red-600 hover:text-red-800 font-medium"
                  >
                    Delete
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openAssignModal(district);
                    }}
                    className="text-xs text-primary hover:text-primaryDark font-medium"
                  >
                    Assign Schools
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto relative">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 pr-8">
              {editingDistrict ? 'Edit District' : 'Create District'}
            </h2>
            
            {editingDistrict && (
              <div className="mb-4 border-b border-gray-200">
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowAddManagerForm(false)}
                    className={`pb-2 px-1 text-sm font-medium ${
                      !showAddManagerForm
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    District Details
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddManagerForm(true)}
                    className={`pb-2 px-1 text-sm font-medium ${
                      showAddManagerForm
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    District Managers ({districtManagers.length})
                  </button>
                </div>
              </div>
            )}
            
            {!showAddManagerForm ? (
              <form onSubmit={handleSaveDistrict} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    District Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

              {!editingDistrict && (
                <div className="pt-2 border-t border-gray-200">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createManager}
                      onChange={(e) => setCreateManager(e.target.checked)}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Create District Manager
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Optionally create a district manager account for this district
                  </p>
                </div>
              )}

              {createManager && !editingDistrict && (
                <div className="space-y-4 pt-2 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700">District Manager Details</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        required={createManager}
                        value={managerForm.firstName}
                        onChange={(e) =>
                          setManagerForm({ ...managerForm, firstName: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required={createManager}
                        value={managerForm.lastName}
                        onChange={(e) =>
                          setManagerForm({ ...managerForm, lastName: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required={createManager}
                      value={managerForm.email}
                      onChange={(e) =>
                        setManagerForm({ ...managerForm, email: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      required={createManager}
                      value={managerForm.password}
                      onChange={(e) =>
                        setManagerForm({ ...managerForm, password: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Minimum 6 characters"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={managerForm.phoneNumber}
                      onChange={(e) =>
                        setManagerForm({ ...managerForm, phoneNumber: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-white py-2 rounded-lg font-semibold hover:bg-primaryDark transition-colors"
                >
                  {editingDistrict ? 'Save Changes' : 'Create District'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
            ) : (
              <div className="space-y-4">
                {/* District Managers List */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">District Managers</h3>
                    <button
                      type="button"
                      onClick={() => {
                        setAssignExistingUser(false);
                        setSelectedUserId('');
                        setManagerForm({
                          firstName: '',
                          lastName: '',
                          email: '',
                          password: '',
                          phoneNumber: '',
                        });
                      }}
                      className="text-sm text-primary hover:text-primaryDark font-medium"
                    >
                      + Add Manager
                    </button>
                  </div>
                  
                  {districtManagers.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 text-center">No district managers assigned</p>
                  ) : (
                    <div className="space-y-2">
                      {districtManagers.map((manager) => (
                        <div
                          key={manager.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {manager.firstName} {manager.lastName}
                            </p>
                            <p className="text-xs text-gray-600">{manager.email}</p>
                            {manager.phoneNumber && (
                              <p className="text-xs text-gray-600">{manager.phoneNumber}</p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await api.patch(`/admin/users/${manager.id}`, {
                                  districtId: null,
                                });
                                setDistrictManagers(districtManagers.filter((m) => m.id !== manager.id));
                                showSuccess('District manager removed successfully');
                              } catch (error) {
                                showError(error.response?.data?.message || 'Failed to remove district manager');
                              }
                            }}
                            className="text-xs text-red-600 hover:text-red-800 font-medium ml-4"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Add District Manager</h3>
                  
                  <div className="mb-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={assignExistingUser}
                        onChange={(e) => {
                          setAssignExistingUser(e.target.checked);
                          setSelectedUserId('');
                          setManagerForm({
                            firstName: '',
                            lastName: '',
                            email: '',
                            password: '',
                            phoneNumber: '',
                          });
                        }}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Assign existing user
                      </span>
                    </label>
                  </div>

                  {assignExistingUser ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Select User *
                        </label>
                        <select
                          value={selectedUserId}
                          onChange={(e) => setSelectedUserId(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          required
                        >
                          <option value="">Choose a user...</option>
                          {availableUsers.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.firstName} {user.lastName} ({user.email}) - {user.role}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Selected user will be assigned DISTRICT role and linked to this district
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!selectedUserId) {
                            showError('Please select a user');
                            return;
                          }
                          try {
                            await api.patch(`/admin/users/${selectedUserId}`, {
                              role: 'DISTRICT',
                              districtId: editingDistrict.id,
                              schoolDistrict: editingDistrict.name,
                            });
                            // Refresh managers list
                            const usersResponse = await api.get('/admin/users', {
                              params: { role: 'DISTRICT', limit: 1000 },
                            });
                            const allDistrictUsers = usersResponse.data.data.users || [];
                            const managers = allDistrictUsers.filter(
                              (user) => user.districtId === editingDistrict.id
                            );
                            setDistrictManagers(managers);
                            setSelectedUserId('');
                            setAssignExistingUser(false);
                            showSuccess('User assigned as district manager successfully');
                          } catch (error) {
                            showError(error.response?.data?.message || 'Failed to assign user as district manager');
                          }
                        }}
                        className="w-full bg-primary text-white py-2 rounded-lg font-semibold hover:bg-primaryDark transition-colors"
                      >
                        Assign User
                      </button>
                    </div>
                  ) : (
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!managerForm.firstName || !managerForm.lastName || !managerForm.email || !managerForm.password) {
                          showError('Please fill all required fields');
                          return;
                        }
                        try {
                          await api.post('/admin/users', {
                            firstName: managerForm.firstName,
                            lastName: managerForm.lastName,
                            email: managerForm.email,
                            password: managerForm.password,
                            phoneNumber: managerForm.phoneNumber || undefined,
                            role: 'DISTRICT',
                            status: 'ACTIVE',
                            districtId: editingDistrict.id,
                            schoolDistrict: editingDistrict.name,
                          });
                          const usersResponse = await api.get('/admin/users', {
                            params: { role: 'DISTRICT', limit: 1000 },
                          });
                          const allDistrictUsers = usersResponse.data.data.users || [];
                          const managers = allDistrictUsers.filter(
                            (user) => user.districtId === editingDistrict.id
                          );
                          setDistrictManagers(managers);
                          setManagerForm({
                            firstName: '',
                            lastName: '',
                            email: '',
                            password: '',
                            phoneNumber: '',
                          });
                          showSuccess('District manager created successfully');
                        } catch (error) {
                          showError(error.response?.data?.message || 'Failed to create district manager');
                        }
                      }}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            First Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={managerForm.firstName}
                            onChange={(e) =>
                              setManagerForm({ ...managerForm, firstName: e.target.value })
                            }
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Last Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={managerForm.lastName}
                            onChange={(e) =>
                              setManagerForm({ ...managerForm, lastName: e.target.value })
                            }
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          required
                          value={managerForm.email}
                          onChange={(e) =>
                            setManagerForm({ ...managerForm, email: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Password *
                        </label>
                        <input
                          type="password"
                          required
                          value={managerForm.password}
                          onChange={(e) =>
                            setManagerForm({ ...managerForm, password: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Minimum 6 characters"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={managerForm.phoneNumber}
                          onChange={(e) =>
                            setManagerForm({ ...managerForm, phoneNumber: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full bg-primary text-white py-2 rounded-lg font-semibold hover:bg-primaryDark transition-colors"
                      >
                        Create Manager
                      </button>
                    </form>
                  )}
                </div>

                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {assigningDistrict && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto relative">
            <button
              type="button"
              onClick={() => {
                setAssigningDistrict(null);
                setSelectedSchoolIds([]);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-gray-800 mb-4 pr-8">
              Assign Schools to {assigningDistrict.name}
            </h2>
            <div className="space-y-3 mb-4">
              {schools.map((school) => (
                <label
                  key={school.id}
                  className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">{school.name}</div>
                    <div className="text-xs text-gray-500">
                      Current district: {school.district || 'None'}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedSchoolIds.includes(school.id)}
                    onChange={() => toggleSchoolSelection(school.id)}
                    className="w-4 h-4 text-primary border-gray-300 rounded"
                  />
                </label>
              ))}
            </div>
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleAssign}
                className="flex-1 bg-primary text-white py-2 rounded-lg font-semibold hover:bg-primaryDark transition-colors"
              >
                Save Assignments
              </button>
              <button
                type="button"
                onClick={() => {
                  setAssigningDistrict(null);
                  setSelectedSchoolIds([]);
                }}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete District"
        message={`Are you sure you want to delete "${deleteConfirm.district?.name}"? This action cannot be undone. This is only allowed if no schools use this district.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={notification.duration}
        />
      )}
    </div>
  );
};

export default Districts;


